import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';

import type {
  Membership,
  MembershipArchiveRequest,
  OrganisationLocation,
  OrganisationTeam,
} from '../domain-types';
import { PersonProfileView, type PlatformPersonDetail } from './administration-directory';

type Assignments = Pick<
  Membership,
  'displayName' | 'jobTitle' | 'locationIds' | 'teamIds' | 'teamLeaderTeamIds' | 'managerUserIds'
>;
type MembershipUpdate = Partial<
  Pick<
    Membership,
    | 'displayName'
    | 'roles'
    | 'status'
    | 'locationIds'
    | 'teamIds'
    | 'teamLeaderTeamIds'
    | 'managerUserIds'
    | 'jobTitle'
  >
>;

export type MemberSecuritySummary = {
  userId: string;
  mfaEnabled: boolean | null;
  lastSeenAt?: string;
};

function activityLabel(lastSeenAt: string | undefined): string {
  if (!lastSeenAt) return 'Not recorded';
  const timestamp = Date.parse(lastSeenAt);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : 'Unavailable';
}

function selectedIds(event: ChangeEvent<HTMLSelectElement>): string[] {
  return Array.from(event.currentTarget.selectedOptions, (option) => option.value);
}

function roleLabel(role: string): string {
  return (
    {
      standard_user: 'Standard User',
      team_leader: 'Team Leader',
      manager: 'Manager',
      tenant_admin: 'Organisation Administrator',
      quality_manager: 'Manager (legacy)',
      auditor: 'Auditor (legacy)',
      viewer: 'Standard User (legacy)',
    }[role] ?? role
  );
}

function LocationTeamAssignments({
  membership,
  locations,
  teams,
  canManageAllAssignments,
  ledTeamIds,
  onUpdate,
}: {
  membership: Membership;
  locations: OrganisationLocation[];
  teams: OrganisationTeam[];
  canManageAllAssignments: boolean;
  ledTeamIds: string[];
  onUpdate: (update: MembershipUpdate) => Promise<void>;
}) {
  const visibleTeams = canManageAllAssignments
    ? teams
    : teams.filter((team) => ledTeamIds.includes(team.id));
  const canManageTeams = canManageAllAssignments || ledTeamIds.length > 0;
  if (!canManageAllAssignments && !canManageTeams) return null;

  return (
    <div className="member-assignments">
      {canManageAllAssignments ? (
        <label>
          Locations
          <select
            multiple
            value={membership.locationIds ?? []}
            onChange={(event) => void onUpdate({ locationIds: selectedIds(event) })}
          >
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {canManageTeams ? (
        <label>
          Teams
          <select
            multiple
            value={membership.teamIds ?? []}
            onChange={(event) => {
              const next = selectedIds(event);
              const preserved = (membership.teamIds ?? []).filter((id) => !ledTeamIds.includes(id));
              void onUpdate({ teamIds: canManageAllAssignments ? next : [...preserved, ...next] });
            }}
          >
            {visibleTeams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {canManageAllAssignments && membership.roles.includes('team_leader') ? (
        <label>
          Teams led
          <select
            multiple
            value={membership.teamLeaderTeamIds ?? []}
            onChange={(event) => void onUpdate({ teamLeaderTeamIds: selectedIds(event) })}
          >
            {(membership.teamIds ?? []).map((teamId) => {
              const team = teams.find((candidate) => candidate.id === teamId);
              return team ? (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ) : null;
            })}
          </select>
        </label>
      ) : null}
    </div>
  );
}

export function TenantMembersPanel({
  visible,
  canAdminister,
  canManageAllAssignments,
  ledTeamIds,
  members,
  securitySummaries,
  locations,
  teams,
  inviteEmail,
  inviteRole,
  onInviteEmailChange,
  onInviteRoleChange,
  onInvite,
  onUpdateMembership,
  onResetMfa,
  currentUserId,
  organisationName = 'Current organisation',
  page = 1,
  total = members.length,
  totalPages = 1,
  search = '',
  roleFilter = '',
  statusFilter = '',
  locationFilter = '',
  teamFilter = '',
  managerFilter = '',
  onSearchChange,
  onRoleFilterChange,
  onStatusFilterChange,
  onLocationFilterChange,
  onTeamFilterChange,
  onManagerFilterChange,
  onPageChange,
  onRequestArchive,
  onDecideArchive,
  onControlledDelete,
  archiveRequests = [],
}: {
  visible: boolean;
  canAdminister: boolean;
  canManageAllAssignments: boolean;
  ledTeamIds: string[];
  members: Membership[];
  securitySummaries?: MemberSecuritySummary[];
  locations: OrganisationLocation[];
  teams: OrganisationTeam[];
  inviteEmail: string;
  inviteRole: string;
  onInviteEmailChange: (value: string) => void;
  onInviteRoleChange: (value: string) => void;
  onInvite: (event: FormEvent<HTMLFormElement>, assignments: Assignments) => Promise<void>;
  onUpdateMembership: (membership: Membership, update: MembershipUpdate) => Promise<void>;
  onResetMfa?: (membership: Membership) => Promise<void>;
  currentUserId?: string;
  organisationName?: string;
  page?: number;
  total?: number;
  totalPages?: number;
  search?: string;
  roleFilter?: string;
  statusFilter?: string;
  locationFilter?: string;
  teamFilter?: string;
  managerFilter?: string;
  onSearchChange?: (value: string) => void;
  onRoleFilterChange?: (value: string) => void;
  onStatusFilterChange?: (value: string) => void;
  onLocationFilterChange?: (value: string) => void;
  onTeamFilterChange?: (value: string) => void;
  onManagerFilterChange?: (value: string) => void;
  onPageChange?: (page: number) => void;
  onRequestArchive?: (membership: Membership) => Promise<void>;
  onDecideArchive?: (userId: string, approve: boolean) => Promise<void>;
  onControlledDelete?: (membership: Membership) => Promise<void>;
  archiveRequests?: MembershipArchiveRequest[];
}) {
  const [inviteLocationIds, setInviteLocationIds] = useState<string[]>([]);
  const [inviteTeamIds, setInviteTeamIds] = useState<string[]>([]);
  const [inviteDisplayName, setInviteDisplayName] = useState('');
  const [inviteJobTitle, setInviteJobTitle] = useState('');
  const [inviteManagerUserIds, setInviteManagerUserIds] = useState<string[]>([]);
  const [selectedMember, setSelectedMember] = useState<Membership>();
  if (!visible) return null;

  if (selectedMember) {
    const security = securitySummaries?.find((item) => item.userId === selectedMember.userId);
    const person: PlatformPersonDetail = {
      userId: selectedMember.userId,
      email: selectedMember.email,
      isPlatformAdmin: false,
      mfaEnabled: security?.mfaEnabled ?? null,
      profile: {
        fullName: selectedMember.displayName ?? selectedMember.email,
        ...(security?.lastSeenAt ? { lastSeenAt: security.lastSeenAt } : {}),
      },
      memberships: [{ ...selectedMember, organisationName }],
    };
    return (
      <PersonProfileView
        person={person}
        viewerCapability={
          selectedMember.userId === currentUserId
            ? 'self'
            : canAdminister
              ? 'organisation_admin'
              : 'team_manager'
        }
        onBack={() => setSelectedMember(undefined)}
      />
    );
  }

  return (
    <section className="members-panel" aria-labelledby="members-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Personnel</p>
          <h2 id="members-title">Organisation personnel</h2>
        </div>
        <div className="personnel-heading-actions">
          {onSearchChange ? (
            <label>
              <span className="visually-hidden">Search personnel</span>
              <input
                placeholder="Search name, email or job title"
                type="search"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </label>
          ) : null}
          <span>{total} people</span>
        </div>
      </div>
      {onSearchChange || onRoleFilterChange || onStatusFilterChange ? (
        <div className="personnel-directory-controls" role="search">
          {onRoleFilterChange ? (
            <label>
              Role
              <select
                value={roleFilter}
                onChange={(event) => onRoleFilterChange(event.target.value)}
              >
                <option value="">All roles</option>
                <option value="standard_user">Standard User</option>
                <option value="team_leader">Team Leader</option>
                <option value="manager">Manager</option>
                <option value="tenant_admin">Organisation Administrator</option>
              </select>
            </label>
          ) : null}
          {onStatusFilterChange ? (
            <label>
              Access
              <select
                value={statusFilter}
                onChange={(event) => onStatusFilterChange(event.target.value)}
              >
                <option value="">All access states</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="archive_requested">Archive requested</option>
                <option value="archived">Archived</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          ) : null}
          {onLocationFilterChange ? (
            <label>
              Location
              <select
                value={locationFilter}
                onChange={(event) => onLocationFilterChange(event.target.value)}
              >
                <option value="">All locations</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {onTeamFilterChange ? (
            <label>
              Team
              <select
                value={teamFilter}
                onChange={(event) => onTeamFilterChange(event.target.value)}
              >
                <option value="">All teams</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {onManagerFilterChange ? (
            <label>
              Manager
              <select
                value={managerFilter}
                onChange={(event) => onManagerFilterChange(event.target.value)}
              >
                <option value="">All managers</option>
                {members
                  .filter((member) => member.status === 'active')
                  .map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.displayName ?? member.email}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      {canAdminister ? (
        <form
          className="personnel-invite-form"
          onSubmit={(event) =>
            void onInvite(event, {
              locationIds: inviteLocationIds,
              teamIds: inviteTeamIds,
              ...(inviteDisplayName.trim() ? { displayName: inviteDisplayName.trim() } : {}),
              ...(inviteJobTitle.trim() ? { jobTitle: inviteJobTitle.trim() } : {}),
              ...(inviteManagerUserIds.length ? { managerUserIds: inviteManagerUserIds } : {}),
              ...(inviteRole === 'team_leader' ? { teamLeaderTeamIds: inviteTeamIds } : {}),
            })
          }
        >
          <label>
            Full name
            <input
              required
              placeholder="Full name"
              value={inviteDisplayName}
              onChange={(event) => setInviteDisplayName(event.target.value)}
            />
          </label>
          <label>
            Personnel email
            <input
              required
              type="email"
              placeholder="person@example.com"
              value={inviteEmail}
              onChange={(event) => onInviteEmailChange(event.target.value)}
            />
          </label>
          <label>
            Job title
            <input
              placeholder="Position or title"
              value={inviteJobTitle}
              onChange={(event) => setInviteJobTitle(event.target.value)}
            />
          </label>
          <label>
            Personnel role
            <select value={inviteRole} onChange={(event) => onInviteRoleChange(event.target.value)}>
              <option value="standard_user">Standard User</option>
              <option value="team_leader">Team Leader</option>
              <option value="manager">Manager</option>
              <option value="tenant_admin">Organisation Administrator</option>
            </select>
          </label>
          <label>
            Locations
            <select
              multiple
              value={inviteLocationIds}
              onChange={(event) => setInviteLocationIds(selectedIds(event))}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Teams
            <select
              multiple
              value={inviteTeamIds}
              onChange={(event) => setInviteTeamIds(selectedIds(event))}
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Direct managers
            <select
              multiple
              value={inviteManagerUserIds}
              onChange={(event) => setInviteManagerUserIds(selectedIds(event))}
            >
              {members
                .filter(
                  (member) =>
                    member.status === 'active' &&
                    member.roles.some((role) =>
                      ['tenant_admin', 'manager', 'team_leader'].includes(role),
                    ),
                )
                .map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.displayName ?? member.email}
                  </option>
                ))}
            </select>
          </label>
          <button type="submit">Invite person</button>
        </form>
      ) : null}
      {canAdminister && onDecideArchive ? (
        <section className="archive-request-queue" aria-labelledby="archive-requests-title">
          <div className="workspace-heading">
            <div>
              <p className="eyebrow">Personnel</p>
              <h3 id="archive-requests-title">Archive requests</h3>
            </div>
            <span>{archiveRequests.length} pending</span>
          </div>
          {archiveRequests.length === 0 ? (
            <p className="empty-state">No archive requests are pending.</p>
          ) : null}
          {archiveRequests.map((request) => {
            const person = members.find((member) => member.userId === request.userId);
            return (
              <article key={request.id}>
                <div>
                  <strong>{person?.displayName ?? person?.email ?? request.userId}</strong>
                  <small>Requested {new Date(request.requestedAt).toLocaleString()}</small>
                  <p>{request.reason}</p>
                </div>
                <div className="member-admin-actions">
                  <button type="button" onClick={() => void onDecideArchive(request.userId, true)}>
                    Approve archive
                  </button>
                  <button type="button" onClick={() => void onDecideArchive(request.userId, false)}>
                    Keep active
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      ) : null}
      <div className="member-list">
        {members.map((membership) => {
          const security = securitySummaries?.find((item) => item.userId === membership.userId);
          return (
            <article key={membership.userId}>
              <div>
                <button
                  className="directory-name-link"
                  type="button"
                  onClick={() => setSelectedMember(membership)}
                >
                  {membership.displayName ?? membership.email}
                </button>
                <small>{membership.roles.map(roleLabel).join(', ')}</small>
                {membership.jobTitle ? <small>{membership.jobTitle}</small> : null}
                <small>
                  Organisation access: {membership.status}
                  {membership.archiveRequestStatus === 'pending' ? ' · archive requested' : ''}
                  {membership.createdAt
                    ? ` · Joined ${new Date(membership.createdAt).toLocaleDateString()}`
                    : ''}
                </small>
                {securitySummaries ? (
                  <div
                    aria-label="Account security summary"
                    className="member-security-summary"
                    role="group"
                  >
                    <small>
                      MFA:{' '}
                      {security?.mfaEnabled === true
                        ? 'Enabled'
                        : security?.mfaEnabled === false
                          ? 'Not enabled'
                          : 'Unavailable'}
                    </small>
                    <small>Last app activity: {activityLabel(security?.lastSeenAt)}</small>
                  </div>
                ) : null}
                <small>
                  Locations:{' '}
                  {(membership.locationIds ?? [])
                    .map((id) => locations.find((location) => location.id === id)?.name)
                    .filter(Boolean)
                    .join(', ') || 'None'}
                </small>
                <small>
                  Teams:{' '}
                  {(membership.teamIds ?? [])
                    .map((id) => teams.find((team) => team.id === id)?.name)
                    .filter(Boolean)
                    .join(', ') || 'None'}
                </small>
                <small>
                  Managers:{' '}
                  {(membership.managerUserIds ?? [])
                    .map(
                      (id) =>
                        members.find((member) => member.userId === id)?.displayName ??
                        members.find((member) => member.userId === id)?.email ??
                        id,
                    )
                    .join(', ') || 'None'}
                </small>
                <LocationTeamAssignments
                  membership={membership}
                  locations={locations}
                  teams={teams}
                  canManageAllAssignments={canManageAllAssignments}
                  ledTeamIds={ledTeamIds}
                  onUpdate={(update) => onUpdateMembership(membership, update)}
                />
              </div>
              {canAdminister ? (
                <div className="member-admin-actions">
                  <label>
                    Full name
                    <input
                      defaultValue={membership.displayName ?? ''}
                      onBlur={(event) => {
                        const displayName = event.target.value.trim();
                        if (displayName && displayName !== membership.displayName) {
                          void onUpdateMembership(membership, { displayName });
                        }
                      }}
                    />
                  </label>
                  <label>
                    Job title
                    <input
                      defaultValue={membership.jobTitle ?? ''}
                      onBlur={(event) => {
                        const jobTitle = event.target.value.trim();
                        if (jobTitle && jobTitle !== membership.jobTitle) {
                          void onUpdateMembership(membership, { jobTitle });
                        }
                      }}
                    />
                  </label>
                  <label>
                    Role
                    <select
                      value={membership.roles[0] ?? 'standard_user'}
                      onChange={(event) =>
                        void onUpdateMembership(membership, {
                          roles: [event.target.value],
                          ...(event.target.value !== 'team_leader'
                            ? { teamLeaderTeamIds: [] }
                            : {}),
                        })
                      }
                    >
                      <option value="standard_user">Standard User</option>
                      <option value="team_leader">Team Leader</option>
                      <option value="manager">Manager</option>
                      <option value="tenant_admin">Organisation Administrator</option>
                    </select>
                  </label>
                  <label>
                    Direct managers
                    <select
                      multiple
                      value={membership.managerUserIds ?? []}
                      onChange={(event) =>
                        void onUpdateMembership(membership, {
                          managerUserIds: selectedIds(event),
                        })
                      }
                    >
                      {members
                        .filter(
                          (candidate) =>
                            candidate.userId !== membership.userId &&
                            candidate.status === 'active' &&
                            candidate.roles.some((role) =>
                              ['tenant_admin', 'manager', 'team_leader'].includes(role),
                            ),
                        )
                        .map((candidate) => (
                          <option key={candidate.userId} value={candidate.userId}>
                            {candidate.email}
                          </option>
                        ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      void onUpdateMembership(membership, {
                        status: membership.status === 'disabled' ? 'active' : 'disabled',
                      })
                    }
                  >
                    {membership.status === 'disabled' ? 'Reactivate' : 'Disable'}
                  </button>
                  {onResetMfa && membership.status !== 'disabled' ? (
                    <button type="button" onClick={() => void onResetMfa(membership)}>
                      Reset MFA
                    </button>
                  ) : null}
                  {membership.archiveRequestStatus === 'pending' && onDecideArchive ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void onDecideArchive(membership.userId, true)}
                      >
                        Approve archive
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDecideArchive(membership.userId, false)}
                      >
                        Keep active
                      </button>
                    </>
                  ) : null}
                  {membership.status === 'archived' && onControlledDelete ? (
                    <button type="button" onClick={() => void onControlledDelete(membership)}>
                      Finalise archived record
                    </button>
                  ) : null}
                </div>
              ) : null}
              {membership.status === 'active' &&
              membership.archiveRequestStatus !== 'pending' &&
              onRequestArchive ? (
                <button type="button" onClick={() => void onRequestArchive(membership)}>
                  Request archive
                </button>
              ) : null}
            </article>
          );
        })}
      </div>
      {onPageChange && totalPages > 1 ? (
        <nav aria-label="Personnel pages" className="personnel-pagination">
          <button disabled={page <= 1} type="button" onClick={() => onPageChange(page - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            type="button"
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </button>
        </nav>
      ) : null}
    </section>
  );
}

export function OrganisationStructurePanel({
  visible,
  editable = true,
  locations,
  teams,
  managerChoices = [],
  busy,
  onSave,
}: {
  visible: boolean;
  editable?: boolean;
  locations: OrganisationLocation[];
  teams: OrganisationTeam[];
  managerChoices?: Membership[];
  busy: boolean;
  onSave: (structure: {
    locations: OrganisationLocation[];
    teams: OrganisationTeam[];
  }) => Promise<void>;
}) {
  const [draftLocations, setDraftLocations] = useState(locations);
  const [draftTeams, setDraftTeams] = useState(teams);
  const [locationName, setLocationName] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [locationPhone, setLocationPhone] = useState('');
  const [locationEmail, setLocationEmail] = useState('');
  const [primaryLocation, setPrimaryLocation] = useState(false);
  const [addressLines, setAddressLines] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('Australia');
  const [teamName, setTeamName] = useState('');
  const [teamLocationIds, setTeamLocationIds] = useState<string[]>([]);
  const [teamManagerUserIds, setTeamManagerUserIds] = useState<string[]>([]);
  const [locationError, setLocationError] = useState('');
  const [teamError, setTeamError] = useState('');

  useEffect(() => setDraftLocations(locations), [locations]);
  useEffect(() => setDraftTeams(teams), [teams]);

  if (!visible) return null;

  const addLocation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocationError('');
    const lines = addressLines
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (
      !locationName.trim() ||
      lines.length === 0 ||
      !suburb.trim() ||
      !state.trim() ||
      !postcode.trim()
    ) {
      setLocationError(
        'Enter a location name, physical address, suburb, state or territory, and postcode.',
      );
      return;
    }
    const location: OrganisationLocation = {
      id: crypto.randomUUID(),
      name: locationName.trim(),
      ...(locationCode.trim() ? { code: locationCode.trim() } : {}),
      ...(locationPhone.trim() ? { phone: locationPhone.trim() } : {}),
      ...(locationEmail.trim() ? { contactEmail: locationEmail.trim() } : {}),
      ...(primaryLocation ? { isPrimary: true } : {}),
      addressLines: lines,
      suburb: suburb.trim(),
      state: state.trim(),
      postcode: postcode.trim(),
      country: country.trim() || 'Australia',
    };
    setDraftLocations((current) => [
      ...(primaryLocation ? current.map((item) => ({ ...item, isPrimary: false })) : current),
      location,
    ]);
    setLocationName('');
    setLocationCode('');
    setLocationPhone('');
    setLocationEmail('');
    setPrimaryLocation(false);
    setAddressLines('');
    setSuburb('');
    setState('');
    setPostcode('');
  };

  const addTeam = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTeamError('');
    if (!teamName.trim()) {
      setTeamError('Enter a team name before adding the team.');
      return;
    }
    setDraftTeams((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        name: teamName.trim(),
        locationIds: teamLocationIds,
        ...(teamManagerUserIds.length > 0 ? { managerUserIds: teamManagerUserIds } : {}),
      },
    ]);
    setTeamName('');
    setTeamLocationIds([]);
    setTeamManagerUserIds([]);
  };

  return (
    <section
      className="organisation-structure-panel"
      aria-labelledby="organisation-structure-title"
    >
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Organisation structure</p>
          <h2 id="organisation-structure-title">Locations and teams</h2>
        </div>
        {!editable ? <span>View only</span> : null}
      </div>
      <p>
        Locations and teams are private to this organisation. Personnel may belong to more than one
        of each.
      </p>
      {editable ? (
        <form className="location-form" onSubmit={addLocation}>
          <label>
            Location name
            <input
              required
              placeholder="Location name"
              value={locationName}
              onChange={(event) => setLocationName(event.target.value)}
            />
          </label>
          <label>
            Location code
            <input
              placeholder="Optional short code"
              value={locationCode}
              onChange={(event) => setLocationCode(event.target.value)}
            />
          </label>
          <label>
            Location email
            <input
              type="email"
              value={locationEmail}
              onChange={(event) => setLocationEmail(event.target.value)}
            />
          </label>
          <label>
            Location phone
            <input
              value={locationPhone}
              onChange={(event) => setLocationPhone(event.target.value)}
            />
          </label>
          <label>
            Primary location
            <input
              checked={primaryLocation}
              type="checkbox"
              onChange={(event) => setPrimaryLocation(event.target.checked)}
            />
          </label>
          <label>
            Physical address
            <textarea
              required
              placeholder="One line per address line"
              value={addressLines}
              onChange={(event) => setAddressLines(event.target.value)}
            />
          </label>
          <label>
            Suburb
            <input
              required
              placeholder="Suburb"
              value={suburb}
              onChange={(event) => setSuburb(event.target.value)}
            />
          </label>
          <label>
            State or territory
            <input
              required
              placeholder="State / territory"
              value={state}
              onChange={(event) => setState(event.target.value)}
            />
          </label>
          <label>
            Postcode
            <input
              required
              placeholder="Postcode"
              value={postcode}
              onChange={(event) => setPostcode(event.target.value)}
            />
          </label>
          <label>
            Country
            <input
              required
              placeholder="Country"
              value={country}
              onChange={(event) => setCountry(event.target.value)}
            />
          </label>
          <button type="submit">Add location</button>
          {locationError ? (
            <p className="auth-error form-error" role="alert">
              {locationError}
            </p>
          ) : null}
        </form>
      ) : null}
      <div className="structure-list">
        {draftLocations.length === 0 ? <p className="empty-state">No locations recorded.</p> : null}
        {draftLocations.map((location) => (
          <article key={location.id}>
            <div>
              <strong>{location.name}</strong>
              {location.code ? <small>{location.code}</small> : null}
              <small>
                {[
                  ...location.addressLines,
                  location.suburb,
                  location.state,
                  location.postcode,
                  location.country,
                ].join(', ')}
              </small>
              {location.phone || location.contactEmail ? (
                <small>{[location.phone, location.contactEmail].filter(Boolean).join(' · ')}</small>
              ) : null}
            </div>
            {editable ? (
              <button
                type="button"
                onClick={() =>
                  setDraftLocations((items) => items.filter((item) => item.id !== location.id))
                }
              >
                Remove
              </button>
            ) : null}
          </article>
        ))}
      </div>
      {editable ? (
        <form className="team-form" onSubmit={addTeam}>
          <label>
            Team name
            <input
              required
              placeholder="Custom team name"
              value={teamName}
              onChange={(event) => setTeamName(event.target.value)}
            />
          </label>
          <label>
            Associated locations
            <select
              multiple
              value={teamLocationIds}
              onChange={(event) => setTeamLocationIds(selectedIds(event))}
            >
              {draftLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Team managers
            <select
              multiple
              value={teamManagerUserIds}
              onChange={(event) => setTeamManagerUserIds(selectedIds(event))}
            >
              {managerChoices
                .filter((membership) => membership.status === 'active')
                .map((membership) => (
                  <option key={membership.userId} value={membership.userId}>
                    {membership.email}
                  </option>
                ))}
            </select>
          </label>
          <button type="submit">Add team</button>
          {teamError ? (
            <p className="auth-error form-error" role="alert">
              {teamError}
            </p>
          ) : null}
        </form>
      ) : null}
      <div className="structure-list">
        {draftTeams.length === 0 ? <p className="empty-state">No teams recorded.</p> : null}
        {draftTeams.map((team) => (
          <article key={team.id}>
            <div>
              <strong>{team.name}</strong>
              <small>
                {team.locationIds
                  .map((id) => draftLocations.find((location) => location.id === id)?.name)
                  .filter(Boolean)
                  .join(', ') || 'No location restriction'}
              </small>
              {(team.managerUserIds ?? []).length > 0 ? (
                <small>
                  Managers:{' '}
                  {(team.managerUserIds ?? [])
                    .map((userId) => managerChoices.find((item) => item.userId === userId)?.email)
                    .filter(Boolean)
                    .join(', ')}
                </small>
              ) : null}
            </div>
            {editable ? (
              <button
                type="button"
                onClick={() =>
                  setDraftTeams((items) => items.filter((item) => item.id !== team.id))
                }
              >
                Remove
              </button>
            ) : null}
          </article>
        ))}
      </div>
      {editable ? (
        <button
          disabled={busy}
          type="button"
          onClick={() => void onSave({ locations: draftLocations, teams: draftTeams })}
        >
          {busy ? 'Saving…' : 'Save organisation structure'}
        </button>
      ) : null}
    </section>
  );
}
